import { Accordion, Stack, Text } from "@chakra-ui/react"
import type { FC } from "react"
import { useMemo } from "react"
import { DATE_FORMATS } from "@/common/constants/date"
import {
  getDateFromTimestamp,
  getTimestampFromIetf,
} from "@/common/helpers/date"
import { pluralize } from "@/common/helpers/string"
import type { CreateProposalFormValues } from "@/createProposal/types/form"
import type { ProposalExecutableCall } from "@/proposal/components/simulation/ProposalImpactOverview"
import HexagateReportSection from "@/proposal/components/simulation/sections/HexagateReportSection"
import SeatbeltReportSection from "@/proposal/components/simulation/sections/SeatbeltReportSection"
import TenderlyReportSection from "@/proposal/components/simulation/sections/TenderlyReportSection"
import type {
  HexagateReport,
  SeatbeltReport,
  TenderlyReport,
} from "@/tallyProposal/hooks/useProposalSimulation"

type Props = {
  tenderly: TenderlyReport
  hexagate: HexagateReport
  seatbelt?: SeatbeltReport
  values?: CreateProposalFormValues
  executableCalls?: ProposalExecutableCall[]
  createdAt?: string
  openByDefault?: boolean
}

const ProposalImpactDetails: FC<Props> = ({
  tenderly,
  hexagate,
  seatbelt,
  values,
  executableCalls,
  createdAt,
  openByDefault = false,
}) => {
  const createdAtLabel = useMemo(() => {
    if (!createdAt) return null

    const timestamp = getTimestampFromIetf(createdAt)
    const date = getDateFromTimestamp(timestamp)

    return date.format(DATE_FORMATS.statusHistoryFullYear)
  }, [createdAt])

  const actionsSimulated = executableCalls?.length ?? 0

  return (
    <Stack spacing={3}>
      {actionsSimulated ? (
        <Text fontSize="sm">
          {actionsSimulated} {pluralize(actionsSimulated, "action", "actions")}{" "}
          simulated {createdAtLabel ? <>• {createdAtLabel}</> : ""}
        </Text>
      ) : null}
      <Accordion
        allowMultiple
        allowToggle
        {...(openByDefault ? { defaultIndex: [0, 1, 2] } : {})}
      >
        <TenderlyReportSection
          executableCalls={executableCalls}
          result={tenderly.result}
          simulations={tenderly.simulations}
          values={values}
        />

        {/* Seatbelt is not available on editing a draft */}
        {seatbelt && seatbelt.result ? (
          <SeatbeltReportSection
            checks={seatbelt?.checks}
            result={seatbelt.result}
          />
        ) : null}

        <HexagateReportSection
          result={hexagate.result}
          threatAnalysis={hexagate.threatAnalysis}
        />
      </Accordion>
    </Stack>
  )
}

export default ProposalImpactDetails
